import { navigate, RouteComponentProps } from '@reach/router';
import compact from 'lodash/compact';
import orderBy from 'lodash/orderBy';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Region, StockEquipment, StockEquipmentStatus } from '../../../interfaces/customer-api';
import PenIcon from '../../assets/pen-icon.svg';
import TrashIcon from '../../assets/trash-can-icon.svg';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useActions, useEffects, useOvermindState } from '../../state';
import { SortOption, useSortOptions, useURLSearchParam } from '../../hooks';
import { cns, formatEstimatedAvailability } from '../../utils';
import { EquipmentIcon } from '../EquipmentIcon/EquipmentIcon';
import { Column } from '../Table/Table';
import { Tooltip } from '../Tooltip/Tooltip';
import { withIsMobile, WithIsMobileProps } from '../withIsMobile/withIsMobile';
import css from './StockEquipmentTable.module.scss';
import { PaginationTable } from '../PaginationTable/PaginationTable';
import { Dropdown } from '../DropDown/DropDown';
import { Button } from '../Button/Button';
import { MenuItem, Menu, Divider } from '@mui/material';
import { Input } from '../Input/Input';
import Search from '../../assets/search.svg';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { MultiSelect } from '../MultiSelect/MultiSelect';
import { EquipmentType } from '../../../interfaces/internal';

const equipmentTypes = Object.values(EquipmentType).sort();

const filterEquipment = (
    equipment: StockEquipment,
    selectedTypes: string[],
    selectedRegions: string[],
    searchTerm: string,
): boolean => {
    // Check equipment type filter
    const typeMatch = selectedTypes.length === 0 || selectedTypes.includes(equipment.type || '');

    // Check region filter - handle both array and non-array cases
    const regionMatch =
        selectedRegions.length === 0 ||
        (Array.isArray(equipment.region) ? equipment.region.some((r) => selectedRegions.includes(r)) : false);

    // Check intput field
    const searchValues = [equipment.configurationNumber, equipment.type, equipment.model, equipment.manufacturingYear]
        .filter((value): value is string => value !== undefined && value !== null)
        .map((value) => value.toLowerCase());
    const searchMatch = searchValues.some((value) => value.includes(searchTerm.toLowerCase()));

    return typeMatch && regionMatch && searchMatch;
};

const SORT_OPTIONS: SortOption<StockEquipment>[] = [
    { key: 'configurationNumber', direction: 'asc', trKey: 'configurationNumberAsc' },
    { key: 'configurationNumber', direction: 'desc', trKey: 'configurationNumberDesc' },
    { key: 'type', direction: 'asc', trKey: 'typeAsc' },
    { key: 'type', direction: 'desc', trKey: 'typeDesc' },
    { key: 'model', direction: 'asc', trKey: 'modelAsc' },
    { key: 'model', direction: 'desc', trKey: 'modelDesc' },
    { key: 'manufacturingYear', direction: 'asc', trKey: 'manufacturingYearAsc' },
    { key: 'manufacturingYear', direction: 'desc', trKey: 'manufacturingYearDesc' },
    { key: 'estimatedAvailability', direction: 'asc', trKey: 'estimatedAvailabilityAsc' },
    { key: 'estimatedAvailability', direction: 'desc', trKey: 'estimatedAvailabilityDesc' },
    { key: 'status', direction: 'asc', trKey: 'statusAsc' },
    { key: 'status', direction: 'desc', trKey: 'statusDesc' },
];

type StockEquipmentTableProps = RouteComponentProps & WithIsMobileProps;
export const StockEquipmentTable: React.FC<StockEquipmentTableProps> = withIsMobile(
    ({ isMobile }: StockEquipmentTableProps) => {
        const { t } = useTranslation(['StockEquipment', 'App']);
        const { user } = useOvermindState();
        const { setSelectedStockEquipment, clearSelectedStockEquipment } = useActions();
        const [selectedEquipmentType, setSelectedEquipmentType] = useState<string[]>([]);
        const [selectedRegion, setSelectedRegion] = useState<string[]>([]);
        const [rowsPerPage, setRowsPerPage] = useState(20);
        const [filterValue, setFilterValue] = useURLSearchParam('search');
        const [stockEquipments, setStockEquipments] = useState<StockEquipment[]>([]);
        const [isStockEquipmentsLoading, setIsStockEquipmentsLoading] = useState(true);
        const [confirmDialogAction, setConfirmDialogAction] = useState<'markAsSold' | 'delete' | null>(null);
        const [equipmentForAction, setEquipmentForAction] = useState<StockEquipment | null>(null);
        const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
        const { api } = useEffects();

        if (user === null) return null;

        const [selectedSortOption, setSelectedSortOption] = useSortOptions(SORT_OPTIONS);

        const fetchStockEquipment = useCallback(async () => {
            setIsStockEquipmentsLoading(true);
            try {
                const data = await api.loadStockEquipments();
                setStockEquipments(data);
            } catch (error) {
                // TODO: add error handling if determined necessary
            } finally {
                setIsStockEquipmentsLoading(false);
            }
        }, [api]);

        useEffect(() => {
            fetchStockEquipment();
        }, [fetchStockEquipment]);

        const handleChangeStatus = async (equipment: StockEquipment, newStatus: StockEquipmentStatus) => {
            if (!equipment.id || equipment.status === newStatus) {
                return;
            }

            const willBeSold = newStatus === StockEquipmentStatus.Sold;

            // When the equipment becomes sold, inventory -1
            let inventory = equipment.inventory;
            if (willBeSold) {
                inventory = inventory && inventory > 0 ? inventory - 1 : inventory;
            }

            await api.updateStockEquipment(equipment.id, {
                ...equipment,
                status: newStatus,
                soldDate: willBeSold ? new Date() : null,
                inventory,
                updatedAt: new Date(),
            });
            await fetchStockEquipment();
        };

        // SOLD returns no options.
        const getStatusMenuOptions = (equipment: StockEquipment): { status: StockEquipmentStatus; label: string }[] => {
            switch (equipment.status) {
                case StockEquipmentStatus.InActive:
                    return [{ status: StockEquipmentStatus.Active, label: t('Mark as active') }];
                case StockEquipmentStatus.Reserved:
                    return [
                        { status: StockEquipmentStatus.Active, label: t('Cancel reservation') },
                        { status: StockEquipmentStatus.Sold, label: t('Mark as sold') },
                    ];
                case StockEquipmentStatus.Active:
                    return [
                        { status: StockEquipmentStatus.InActive, label: t('Mark as inactive') },
                        { status: StockEquipmentStatus.Reserved, label: t('Mark as reserved') },
                        { status: StockEquipmentStatus.Sold, label: t('Mark as sold') },
                    ];
                default:
                    return [];
            }
        };

        const onRowClick = async (equipment: StockEquipment) => {
            setSelectedStockEquipment(equipment);
            await navigate(`${window.location.pathname}/${equipment.id}`);
        };

        const handleEdit = async (equipment: StockEquipment) => {
            setSelectedStockEquipment(equipment);
            navigate('stock-equipment/register');
        };

        const handleDuplicate = (equipment: StockEquipment) => {
            // NOT copied: id, documentKeys (attachments), soldDate, inventory, addedDate, createdAt, updatedAt
            const draft: StockEquipment = {
                configurationNumber: `Copy of ${equipment.configurationNumber}`,
                description: equipment.description,
                type: equipment.type,
                model: equipment.model,
                engineType: equipment.engineType,
                mauLocation: equipment.mauLocation,
                estimatedAvailability: equipment.estimatedAvailability,
                manufacturingYear: equipment.manufacturingYear,
                region: equipment.region,
                price: equipment.price,
                currency: equipment.currency,
                contactEmail: equipment.contactEmail,
                status: StockEquipmentStatus.Active,
            };

            setSelectedStockEquipment(draft);
            navigate('stock-equipment/register');
        };

        const handleDelete = async (equipment: StockEquipment) => {
            if (equipment.id) {
                await api.deleteStockEquipment(equipment.id);
                await fetchStockEquipment();
            }
        };

        const handleColumnSort = (key: keyof StockEquipment) => {
            const isCurrentSortKey = selectedSortOption.key === key;
            const targetDirection = isCurrentSortKey && selectedSortOption.direction === 'asc' ? 'desc' : 'asc';

            const targetSortOption = SORT_OPTIONS.find(
                (option) => option.key === key && option.direction === targetDirection,
            );

            if (!targetSortOption) return;
            setSelectedSortOption(targetSortOption);
        };

        const columns: Column<StockEquipment>[] = compact([
            {
                title: '',
                accessor: ({ type }: StockEquipment) => <EquipmentIcon type={type ?? null} />,
                className: cns(css.centered, css.type),
            },
            {
                title: t('Configuration number'),
                accessor: ({ configurationNumber }: StockEquipment) => {
                    return <span data-cy="stock-equipment-row">{configurationNumber}</span>;
                },
                className: css.name,
                onSort: () => handleColumnSort('configurationNumber'),
                sortDirection: selectedSortOption.key === 'configurationNumber' ? selectedSortOption.direction : null,
            },
            !isMobile && {
                title: t('Description'),
                accessor: ({ description }: StockEquipment) => {
                    return description;
                },
                className: css.description,
            },
            !isMobile && {
                title: t('Type'),
                accessor: ({ type }: StockEquipment) => {
                    return type;
                },
                className: css.description,
                onSort: () => handleColumnSort('type'),
                sortDirection: selectedSortOption.key === 'type' ? selectedSortOption.direction : null,
            },
            !isMobile && {
                title: t('Model'),
                accessor: ({ model }: StockEquipment) => {
                    return model;
                },
                className: css.model,
                onSort: () => handleColumnSort('model'),
                sortDirection: selectedSortOption.key === 'model' ? selectedSortOption.direction : null,
            },
            !isMobile && {
                title: t('Estimated availability'),
                accessor: ({ estimatedAvailability }: StockEquipment) =>
                    formatEstimatedAvailability(estimatedAvailability, t('Ready')),
                className: css.site,
                onSort: () => handleColumnSort('estimatedAvailability'),
                sortDirection:
                    selectedSortOption.key === 'estimatedAvailability' ? selectedSortOption.direction : null,
            },
            !isMobile && {
                title: t('Manufacturing year'),
                accessor: ({ manufacturingYear }: StockEquipment) => {
                    return manufacturingYear;
                },
                className: css.contracts,
                onSort: () => handleColumnSort('manufacturingYear'),
                sortDirection:
                    selectedSortOption.key === 'manufacturingYear' ? selectedSortOption.direction : null,
            },
            !isMobile &&
                user.access.stockEquipment.create && {
                    title: t('Status'),
                    accessor: (equipment: StockEquipment) => (equipment.status ? t(equipment.status) : ''),
                    className: css.description,
                    onSort: () => handleColumnSort('status'),
                    sortDirection:
                        selectedSortOption.key === 'status' ? selectedSortOption.direction : null,
                },
            !isMobile &&
                user.access.stockEquipment.create && {
                    title: '',
                    accessor: (equipment: StockEquipment) => {
                        const editable =
                            equipment.status === StockEquipmentStatus.Active ||
                            equipment.status === StockEquipmentStatus.InActive;
                        return (
                        <div className={css.actionButtons}>
                            <Tooltip title={t('Edit')} placement="top">
                                <button
                                    className={editable ? css.actionButton : css.actionButtonDisabled}
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent row click
                                        handleEdit(equipment);
                                    }}
                                    disabled={!editable}
                                >
                                    <PenIcon />
                                </button>
                            </Tooltip>
                            <Tooltip title={t('Delete')} placement="top">
                                <button
                                    className={css.actionButton}
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent row click
                                        setEquipmentForAction(equipment);
                                        setConfirmDialogAction('delete');
                                    }}
                                >
                                    <TrashIcon />
                                </button>
                            </Tooltip>
                            <Tooltip title={t('More options')} placement="top">
                                <button
                                    className={css.actionButton}
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent row click
                                        setEquipmentForAction(equipment);
                                        setStatusMenuAnchor(e.currentTarget);
                                    }}
                                >
                                    <MoreHorizIcon />
                                </button>
                            </Tooltip>
                        </div>
                        );
                    },
                    className: css.subscription,
                },
        ]);

        const handleChangeRowsPerPage = (value: number) => {
            setRowsPerPage(value);
        };

        const filteredAndSortedData = useMemo(() =>
            orderBy(
                stockEquipments.filter((equipment) =>
                    filterEquipment(equipment, selectedEquipmentType, selectedRegion, filterValue),
                ),
                [selectedSortOption.key],
                [selectedSortOption.direction],
            ),
        [stockEquipments, selectedEquipmentType, selectedRegion, filterValue, selectedSortOption]);

        return (            <>
                {
                    // Show search field and create button only to admins
                    <div className={css.searchBarContainer}>
                        <Input
                            containerProps={{
                                className: css.searchField,
                            }}
                            icon={!isMobile ? <Search /> : undefined}
                            id="my-equipment-search"
                            inputProps={{
                                onChange: ({ target }) => setFilterValue(target.value),
                                placeholder: !isMobile
                                    ? t('Search equipment with name, serial number, description, model or site...')
                                    : t('Search with name, serial number or description'),
                                type: 'text',
                                value: filterValue,
                                className: css.searchFieldInput,
                            }}
                        />
                        {user.isAdmin && user.access.stockEquipment.create && (
                            <Button
                                id="create-stock-equipment-button"
                                variant="primary"
                                linkTo="/stock-equipment/register"
                                text={`+ ${t('Create new', { ns: 'App' })}`}
                                onClick={() => clearSelectedStockEquipment()}
                                className={css.registerButton}
                            />
                        )}
                    </div>
                }
                <ConfirmationDialog
                    isOpen={confirmDialogAction !== null}
                    onClose={() => {
                        setConfirmDialogAction(null);
                        setEquipmentForAction(null);
                    }}
                    onConfirm={async () => {
                        if (equipmentForAction) {
                            if (confirmDialogAction === 'markAsSold') {
                                await handleChangeStatus(equipmentForAction, StockEquipmentStatus.Sold);
                            } else if (confirmDialogAction === 'delete') {
                                await handleDelete(equipmentForAction);
                            }
                        }
                        setConfirmDialogAction(null);
                        setEquipmentForAction(null);
                    }}
                    message={
                        confirmDialogAction === 'markAsSold'
                            ? t('Mark equipment as sold. This action can’t be undone.')
                            : t('Delete this equipment. This action can’t be undone.')
                    }
                    title={confirmDialogAction === 'markAsSold' ? t('Mark as sold') : t('Delete')}
                    confirmText={t('Confirm')}
                    cancelText={t('Cancel')}
                />
                <Menu
                    anchorEl={statusMenuAnchor}
                    open={statusMenuAnchor !== null}
                    onClose={() => {
                        setStatusMenuAnchor(null);
                        setEquipmentForAction(null);
                    }}
                >
                    {equipmentForAction && (
                        <MenuItem
                            key="duplicate"
                            sx={{ fontSize: '14px' }}
                            onClick={() => {
                                const target = equipmentForAction;
                                setStatusMenuAnchor(null);
                                setEquipmentForAction(null);
                                if (target) handleDuplicate(target);
                            }}
                        >
                            {t('Copy and create new')}
                        </MenuItem>
                    )}
                    {equipmentForAction && getStatusMenuOptions(equipmentForAction).length > 0 && (
                        <Divider />
                    )}
                    {equipmentForAction &&
                        getStatusMenuOptions(equipmentForAction).map((option) => (
                            <MenuItem
                                key={option.status}
                                sx={{ fontSize: '14px' }}
                                onClick={async () => {
                                    setStatusMenuAnchor(null);
                                    if (option.status === StockEquipmentStatus.Sold) {
                                        // To mark as sold, open ConfirmationDialog
                                        setConfirmDialogAction('markAsSold');
                                    } else {
                                        await handleChangeStatus(equipmentForAction, option.status);
                                        setEquipmentForAction(null);
                                    }
                                }}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                </Menu>
                <div className={css.dropdownContainer}>
                    <MultiSelect
                        value={selectedEquipmentType}
                        onChange={setSelectedEquipmentType}
                        options={equipmentTypes}
                        placeholder={t('Select equipment type')}
                    />
                    {user.isAdmin && (
                        <MultiSelect
                            value={selectedRegion}
                            onChange={setSelectedRegion}
                            options={Object.values(Region)}
                            placeholder={t('Select region')}
                        />
                    )}
                    <Dropdown
                        handleChange={handleChangeRowsPerPage}
                        label={`${t('Show on page')}:`}
                        options={[
                            { id: 20, label: '20' },
                            { id: 40, label: '40' },
                            { id: 60, label: '60' },
                        ]}
                        selected={rowsPerPage}
                    />
                </div>
                <PaginationTable
                    columns={columns}
                    data={filteredAndSortedData}
                    className={css.equipmentTable}
                    onRowClick={onRowClick}
                    id="stock-equipment-table"
                    isLoading={isStockEquipmentsLoading}
                    rowsPerPage={rowsPerPage}
                />
            </>
        );
    },
);
